import { PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Button, Skeleton, Space, Tabs } from "antd";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMe } from "@/modules/auth/hooks/useAuth";
import { EmptyState } from "@/shared/components/EmptyState";
import { PageHeader } from "@/shared/components/PageHeader";
import { type UrlParamPatch, useUrlParams, useUrlTableState } from "@/shared/hooks/useUrlState";
import { TaskEditModal } from "../components/TaskEditModal";
import { TaskFormModal } from "../components/TaskFormModal";
import { TaskStatusModal } from "../components/TaskStatusModal";
import { TaskTable } from "../components/TaskTable";
import { UrgentTaskFormModal } from "../components/UrgentTaskFormModal";
import { useTasksStore } from "../store/tasks.store";
import type { TaskListFilters } from "../types";

type TasksTab = "all" | "urgent";

export function TasksPage() {
	const { searchParams, setParams } = useUrlParams();
	const { page, limit, setPage, setFilters } = useUrlTableState();
	const navigate = useNavigate();

	const activeTab: TasksTab = searchParams.get("tab") === "urgent" ? "urgent" : "all";
	const filters = useMemo<TaskListFilters>(
		() => ({
			status: (searchParams.get("status") as TaskListFilters["status"]) ?? undefined,
			priority: (searchParams.get("priority") as TaskListFilters["priority"]) ?? undefined,
			brigadeId: searchParams.get("brigadeId") ?? undefined,
			dueDateFrom: searchParams.get("dueDateFrom") ?? undefined,
			dueDateTo: searchParams.get("dueDateTo") ?? undefined,
		}),
		[searchParams]
	);

	const { data: meUser, isLoading: isMeLoading } = useMe();
	const openCreateModal = useTasksStore((s) => s.openCreateModal);
	const openUrgentModal = useTasksStore((s) => s.openUrgentModal);

	const parentOrgId = useMemo(
		() =>
			meUser?.organizations?.find((o) => o.isPrimary)?.id ?? meUser?.organizations?.[0]?.id ?? "",
		[meUser]
	);

	const isUrgentTab = activeTab === "urgent";
	// Urgent tab is a preset: it pins priority=URGENT (sent history of urgent tasks).
	const effectiveFilters = useMemo<TaskListFilters>(
		() => (isUrgentTab ? { ...filters, priority: "URGENT" } : filters),
		[isUrgentTab, filters]
	);

	const handleFiltersChange = (next: TaskListFilters) => {
		const patch: UrlParamPatch = {
			status: next.status ?? null,
			brigadeId: next.brigadeId ?? null,
			dueDateFrom: next.dueDateFrom ?? null,
			dueDateTo: next.dueDateTo ?? null,
		};
		// On the urgent tab the pinned URGENT priority must not leak into shared filter state.
		if (!isUrgentTab) {
			patch.priority = next.priority ?? null;
		}
		setFilters(patch);
	};

	const handleTabChange = (key: string) => {
		// Tab switch + page reset in a single history entry.
		setParams({ tab: key === "all" ? null : key, page: null });
	};

	return (
		<div>
			<PageHeader
				title="Topshiriqlar"
				action={
					<Space>
						<Button
							danger
							icon={<ThunderboltOutlined />}
							onClick={openUrgentModal}
							id="tasks-urgent-btn"
						>
							Tezkor topshiriq
						</Button>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={openCreateModal}
							id="tasks-create-btn"
						>
							Qo'shish
						</Button>
					</Space>
				}
			/>

			{isMeLoading ? (
				<Skeleton active paragraph={{ rows: 5 }} />
			) : parentOrgId ? (
				<>
					<Tabs
						activeKey={activeTab}
						onChange={handleTabChange}
						items={[
							{ key: "all", label: "Barcha topshiriqlar" },
							{ key: "urgent", label: "Tezkor topshiriqlar" },
						]}
					/>
					<TaskTable
						page={page}
						limit={limit}
						onPageChange={setPage}
						scopeOrganizationId={parentOrgId}
						filters={effectiveFilters}
						onFiltersChange={handleFiltersChange}
						priorityLocked={isUrgentTab}
						onRowClick={(task) => navigate(`/tasks/${task.id}`)}
					/>
					<TaskFormModal />
					<UrgentTaskFormModal />
					<TaskEditModal />
					<TaskStatusModal />
				</>
			) : (
				<EmptyState description="Tashkilot topilmadi — topshiriqlarni ko'rish uchun tashkilotga qo'shilishingiz kerak" />
			)}
		</div>
	);
}
